import { useCallback, useEffect, useRef, useState } from 'react';
import { SongShakeFX, fxSupported } from '../_helpers/fxEngine.js';
import {
  ensureFxPackagesAndAlternatives,
  loadFxConfig,
  loadFxLoadout,
  photosensitiveFxPackage,
  registeredFxPackage,
  resolveFxPackage,
} from '../_helpers/fxPackages.js';
import { reducePackage } from '../_helpers/fxReduceEffects.js';
import { FX_PACKAGE_TYPE, isFxItemType } from '../_helpers/fxItems.js';

/** How the preview runs. Tests pass shorter times. */
export const FX_PREVIEW_TIMING = Object.freeze({
  // A vibe set's build climbs from Start to Peak over this long, in steps.
  buildMs: 2400,
  buildSteps: 8,
  // A boost animation starts from a small build held this long.
  leadInMs: 700,
  leadInLevel: 0.35,
  // The afterglow plays this long before the preview ends.
  afterglowMs: 4500,
});

/** Phones and tablets draw at 1×, as the stream page's stage does (ENG-804). */
const MOBILE_QUERY = '(max-width: 700px), (pointer: coarse)';
const isMobile = () => {
  try {
    return Boolean(window.matchMedia?.(MOBILE_QUERY)?.matches);
  } catch (_e) {
    return false;
  }
};

/**
 * A live preview of an FX item (ENG-816) on a sample album card: a vibe set
 * plays build → boost → afterglow, a boost animation plays the full boost
 * (with the default vibe set's build and afterglow around it).
 *
 * The boost in a vibe set's preview (ENG-866) is the vibe set's suggested
 * boost (`suggestedBoostId`, the item's `defaultBoostPackageId`), else the
 * listener's equipped boost, else the default. It is only a preview: boosts
 * and vibe sets are separate items, and a vibe set never chooses the boost
 * that plays when someone boosts.
 *
 * In Photosensitivity mode (`reduceEffects`) each package is replaced by its
 * own photosensitive alternative, else the default of its type
 * (`photosensitiveFxPackage`), then reduced as below.
 *
 * It runs on a stage of its own (`SongShakeFX.createStage` on the preview's
 * container), never the stream page's: nothing here reads `FxContext`, adds a
 * card to the queue's stage or switches the viewer's vibe set, so a preview
 * never touches the live queue. Packages go into the engine's shared registry
 * as is (`ensureFxPackages`) and are never mutated; reduced variants are
 * per-preview copies.
 *
 * `reduceEffects` is whether effects play reduced (`useReduceEffects`, ENG-836): on, the
 * vibe set and the boost play without flash or lightning and the stage's
 * reduced-motion mode is on. Changing it (or the item) restarts the preview.
 *
 * Attach `containerRef` to the stage element, `rowRef` to the element holding
 * the cards (`ssfx-row`), and `cardRef` / `innerRef` / `coverRef` to the
 * sample card, its moving wrapper and its cover; `neighbourRef` is the card
 * the boost passes. Returns `{ status, replay, containerRef, rowRef, cardRef,
 * innerRef, coverRef, neighbourRef }`, `status` being
 * 'loading' | 'playing' | 'done' | 'unsupported' (no Canvas2D, Path2D or
 * ResizeObserver) | 'failed' (no package, or the engine refused it).
 */
export const useFxPreview = ({
  itemType,
  packageId,
  suggestedBoostId = null,
  reduceEffects = false,
  timing = FX_PREVIEW_TIMING,
} = {}) => {
  const supported = fxSupported();
  const [status, setStatus] = useState(supported ? 'loading' : 'unsupported');
  const [run, setRun] = useState(0);
  const [container, setContainer] = useState(null);
  const rowRef = useRef(null);
  const cardRef = useRef(null);
  const innerRef = useRef(null);
  const coverRef = useRef(null);
  const neighbourRef = useRef(null);
  const timingRef = useRef(timing);
  timingRef.current = timing;

  useEffect(() => {
    if (!supported) {
      setStatus('unsupported');
      return undefined;
    }
    if (!container || !isFxItemType(itemType) || !packageId) return undefined;
    let cancelled = false;
    let stage = null;
    const timers = new Set();
    const sleep = (ms) => new Promise((resolve) => {
      const id = setTimeout(() => { timers.delete(id); resolve(); }, Math.max(0, ms));
      timers.add(id);
    });
    const onVisibility = () => {
      if (!stage) return;
      if (document.hidden) stage.stop();
      else stage.start();
    };
    setStatus('loading');

    (async () => {
      const cfg = await loadFxConfig();
      const isVibeSet = itemType === 'vibe_set';
      // A vibe set previews its suggested boost, else the listener's own, else the default.
      const equippedBoost = isVibeSet && !suggestedBoostId ? (await loadFxLoadout(cfg.defaults))?.boost : null;
      const wantedBoost = isVibeSet ? suggestedBoostId || equippedBoost || cfg.defaults.boost : packageId;
      await ensureFxPackagesAndAlternatives([packageId, wantedBoost]);
      if (cancelled) return;
      const packageType = FX_PACKAGE_TYPE[itemType];
      if (!registeredFxPackage(packageId, packageType)) throw { code: 'fx_package_not_found' };
      const shown = (id, type) => (reduceEffects ? photosensitiveFxPackage(id, type, cfg.defaults) : resolveFxPackage(id, type, cfg.defaults));
      const vibeId = shown(isVibeSet ? packageId : cfg.defaults.vibe, 'vibe');
      const boostId = shown(wantedBoost, 'boost');
      const T = timingRef.current;

      stage = SongShakeFX.createStage({
        container,
        shakeEl: rowRef.current,
        vibe: reduceEffects ? reducePackage(vibeId, 'vibe') : vibeId,
        rules: cfg.rulesId,
        maxDpr: isMobile() ? 1 : 2,
        ...(reduceEffects ? { reducedMotion: true } : {}),
      });
      document.addEventListener('visibilitychange', onVisibility);
      if (document.hidden) stage.stop();
      const card = stage.addCard({ id: 'fx-preview', el: cardRef.current, inner: innerRef.current, cover: coverRef.current });
      if (neighbourRef.current) stage.addCard({ id: 'fx-preview-neighbour', el: neighbourRef.current });
      setStatus('playing');

      // The build: Start to Peak for a vibe set, a small one before a boost animation.
      let level = T.leadInLevel;
      if (itemType === 'vibe_set') {
        const steps = Math.max(1, T.buildSteps);
        for (let i = 1; i <= steps; i += 1) {
          level = i / steps;
          card.setBuild({ level, visible: 1, lit: true, rel: 1 });
          await sleep(T.buildMs / steps);
          if (cancelled) return;
        }
      } else {
        card.setBuild({ level, visible: 1, lit: true, rel: 1 });
        await sleep(T.leadInMs);
        if (cancelled) return;
      }

      // The boost, then the afterglow the engine starts at its end.
      await card.boost(reduceEffects ? reducePackage(boostId, 'boost') : boostId, {
        target: neighbourRef.current ?? undefined,
        level,
      });
      if (cancelled) return;
      await sleep(T.afterglowMs);
      if (cancelled) return;
      card.clearAfterglow();
      setStatus('done');
    })().catch(() => {
      if (!cancelled) setStatus('failed');
    });

    return () => {
      cancelled = true;
      timers.forEach((id) => clearTimeout(id));
      timers.clear();
      document.removeEventListener('visibilitychange', onVisibility);
      stage?.destroy();
      stage = null;
    };
  }, [supported, container, itemType, packageId, suggestedBoostId, reduceEffects, run]);

  const containerRef = useCallback((node) => setContainer(node), []);
  const replay = useCallback(() => setRun((n) => n + 1), []);

  return { status, replay, containerRef, rowRef, cardRef, innerRef, coverRef, neighbourRef };
};

export default useFxPreview;
